import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, UserCog, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOperatorsSection = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({ name: '', role: '', phone: '', email: '' });
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchOperators(); }, []);

  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/operators`, { headers: getAuthHeaders() });
      setOperators(response.data);
    } catch (error) {
      setOperators([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOperator) {
        await axios.put(`${API}/operators/${editingOperator.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Operatore aggiornato!');
      } else {
        await axios.post(`${API}/operators`, formData, { headers: getAuthHeaders() });
        toast.success('Operatore aggiunto!');
      }
      setDialogOpen(false);
      resetForm();
      fetchOperators();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (op) => {
    setEditingOperator(op);
    setFormData({ name: op.name, role: op.role, phone: op.phone || '', email: op.email || '', color: op.color || '#3b82f6' });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/operators/${deleteTarget}`, { headers: getAuthHeaders() });
      toast.success('Operatore rimosso');
      fetchOperators();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
    setDeleteTarget(null);
  };

  const resetForm = () => {
    setFormData({ name: '', role: '', phone: '', email: '', color: '#3b82f6' });
    setEditingOperator(null);
  };

  const presetColors = [
    { name: 'Azzurro', value: '#3b82f6' },
    { name: 'Verde', value: '#22c55e' },
    { name: 'Rosso', value: '#ef4444' },
    { name: 'Viola', value: '#a855f7' },
    { name: 'Arancio', value: '#f97316' },
    { name: 'Rosa', value: '#ec4899' },
    { name: 'Giallo', value: '#eab308' },
    { name: 'Teal', value: '#14b8a6' },
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="operators-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Gestione Operatori</h2>
          <p className="text-muted-foreground text-sm">Aggiungi, modifica o rimuovi collaboratori</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="add-operator-btn">
              <Plus className="h-4 w-4 mr-2" /> Nuovo
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingOperator ? 'Modifica' : 'Nuovo'} Operatore</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome *</Label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Nome completo" />
              </div>
              <div>
                <Label>Ruolo *</Label>
                <Input required value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} placeholder="Es: Barbiere, Apprendista" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Telefono</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+39 333..." />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@..." />
                </div>
              </div>
              <div>
                <Label>Colore Agenda</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {presetColors.map(c => (
                    <button key={c.value} type="button" onClick={() => setFormData({ ...formData, color: c.value })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${formData.color === c.value ? 'border-foreground scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c.value }} title={c.name} data-testid={`color-${c.value}`} />
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="flex-1">Annulla</Button>
                <Button type="submit" className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90">{editingOperator ? 'Aggiorna' : 'Aggiungi'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {operators.length === 0 ? (
        <div className="text-center py-12">
          <UserCog className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">Nessun operatore registrato</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {operators.map((op) => (
            <Card key={op.id} className="card-elegant" data-testid={`operator-${op.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: op.color || '#3b82f6' }}>
                      <UserCog className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{op.name}</h3>
                      <p className="text-sm text-muted-foreground">{op.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(op)} data-testid={`edit-operator-${op.id}`}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => setDeleteTarget(op.id)} data-testid={`delete-operator-${op.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Conferma Eliminazione</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Sei sicuro di voler eliminare questo operatore? Questa azione non puo' essere annullata.</p>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="flex-1">Annulla</Button>
            <Button variant="destructive" onClick={handleDelete} className="flex-1" data-testid="confirm-delete-operator">Elimina</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOperatorsSection;
